//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import { useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';

import { ProductImageUpload } from '@campaigns/components/shared/product-details-modal/details/ProductImageUpload';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import { isValidImageUrl } from '@campaigns/constants/sampling/SamplingProductValidationRulesConstants';
import { useProductModal } from '@campaigns/contexts/ProductModalContext';
import { EditableBVSamplingProduct } from '@campaigns/types/sampling/SamplingProductTypes';
import Badge, { BadgeType } from '@components/atoms/badge/Badge';
import { PrimaryLinkButton } from '@components/atoms/button/Button';
import { ImageWithErrorHandling } from '@components/atoms/image-with-error-handling/ImageWithErrorHandling';
import LabeledCheckbox from '@components/atoms/labeled-checkbox/LabeledCheckbox';
import { BodyText, CaptionText } from '@components/atoms/text/Text';
import { isDefined, isTruthy } from '@utils/filterPredicates';

interface ProductImageSectionProps {
  isViewOnly?: boolean;
  isDisabled?: boolean;
}

export const ProductImageSection = ({
  isDisabled = false,
  isViewOnly = false,
}: ProductImageSectionProps) => {
  const {
    formState: { errors },
    getValues,
    setValue,
    watch,
  } = useFormContext<EditableBVSamplingProduct>();

  const {
    handleMainImageSelect,
    handleVariantImageSelect,
    isImageForAllVariants,
    mainProductImage,
    setIsImageForAllVariants,
    setShowImagePreview,
    showImagePreview,
  } = useProductModal();

  const imgUrl = watch('imgUrl');
  const imageError = errors.imgUrl?.message;
  const hasImage = isTruthy(imgUrl) && showImagePreview;

  const updateVariantImages = (newImageUrl: string) => {
    const currentValues = getValues();
    const updatedVariations = currentValues.variations.map((variation) => ({
      ...variation,
      imgUrl: newImageUrl,
    }));
    setValue('variations', updatedVariations, {
      shouldValidate: true,
    });
    setIsImageForAllVariants(true);
  };

  const clearExistingImage = () => {
    const currentImgUrl = getValues('imgUrl');

    if (currentImgUrl?.startsWith('blob:')) {
      URL.revokeObjectURL(currentImgUrl);
    }

    setValue('imgUrl', '', {
      shouldValidate: true,
    });
  };

  const handleUseForAllVariants = (checked: boolean) => {
    setIsImageForAllVariants(checked);
    if (checked && imgUrl) {
      updateVariantImages(imgUrl);
    }
  };

  const handleFileSelect = (file: File | undefined) => {
    clearExistingImage();

    if (file) {
      const blobUrl = URL.createObjectURL(file);
      setValue('imgUrl', blobUrl, {
        shouldValidate: true,
      });
      setShowImagePreview(true);

      if (isImageForAllVariants) {
        updateVariantImages(blobUrl);
      }
    } else {
      setValue('imgUrl', '', {
        shouldValidate: true,
      });
    }
    handleMainImageSelect(file);
  };

  const handlePreview = () => {
    const currentUrl = watch('imgUrl');
    if (isValidImageUrl(currentUrl)) {
      setShowImagePreview(true);
    }
  };

  const handleClearImage = () => {
    const currentValues = getValues();
    const currentImgUrl = currentValues.imgUrl;

    // If we have a main image file and variants might be using it we should clone the file for each variant
    if (mainProductImage?.file && currentImgUrl?.startsWith('blob:')) {
      const variantIndices = currentValues.variations
        .map((variation, index) =>
          variation.imgUrl === currentImgUrl ? index : undefined,
        )
        .filter(isDefined);

      if (variantIndices.length > 0) {
        const updatedVariations = [...currentValues.variations];

        for (const index of variantIndices) {
          const fileClone = new File(
            [mainProductImage.file],
            mainProductImage.file.name,
            {
              type: mainProductImage.file.type,
            },
          );

          handleVariantImageSelect(index, fileClone);

          // Create a new blob URL for the preview
          const newBlobUrl = URL.createObjectURL(fileClone);

          updatedVariations[index] = {
            ...updatedVariations[index],
            imgUrl: newBlobUrl,
          };
        }

        setValue('variations', updatedVariations, {
          shouldValidate: true,
        });
      }
    }

    clearExistingImage();
    setShowImagePreview(false);
    handleMainImageSelect(undefined);
  };

  return (
    <Container>
      <PreviewHeader>
        <BodyText>Image</BodyText>
        {!isViewOnly && (
          <Badge
            size='small'
            text='Visible to Participants'
            type={BadgeType.Secondary}
          />
        )}
      </PreviewHeader>

      {!isViewOnly && !hasImage && (
        <CaptionText>
          File types supported: 'jpeg', 'bmp', 'gif', 'png', and'webp'
        </CaptionText>
      )}

      {!isViewOnly && (
        <LabeledCheckbox
          handleClick={handleUseForAllVariants}
          isChecked={isImageForAllVariants}
          label='Use for all variants'
          trackingId={
            SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_USE_FOR_ALL_VARIANTS_CHECKBOX
          }
        />
      )}

      {hasImage ? (
        <PreviewContainer>
          <PreviewImage alt='Product preview' src={imgUrl} />
          {!isViewOnly && (
            <PrimaryLinkButton
              onClick={handleClearImage}
              trackingId={
                SAMPLING_CAMPAIGNS_TRACKING_ID.PRODUCT_MODAL_REMOVE_IMAGE_BUTTON
              }
            >
              Remove image
            </PrimaryLinkButton>
          )}
        </PreviewContainer>
      ) : (
        <ProductImageUpload
          error={imageError}
          isDisabled={isDisabled}
          isViewOnly={isViewOnly}
          onClearImage={handleClearImage}
          onFileSelect={handleFileSelect}
          onPreview={handlePreview}
          updateVariantImages={updateVariantImages}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacings[3]};
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PreviewImage = styled(ImageWithErrorHandling)`
  ${({ theme }) => css`
    width: ${theme.smallMeasure};
    height: ${theme.smallMeasure};
    border: 1px solid ${theme.colors.gray2};
    border-radius: ${theme.borderRadii[2]};
    object-fit: contain;
  `}
`;

const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacings[1]};
`;
